import { memo, useState, useCallback } from 'react';
import { HStack, VStack } from '@cbhq/cds-web/layout';
import {
  Link,
  TextTitle3,
  TextHeadline,
  TextBody,
  TextLabel1,
  TextLabel2,
} from '@cbhq/cds-web/typography';
import { Icon } from '@cbhq/cds-web/icons';
import { RemoteImage } from '@cbhq/cds-web/media';
import { PressableOpacity } from '@cbhq/cds-web/system';
import { SummaryCard } from '@/components/common/SummaryCard';
import { type CorporateAction } from '@/features/tokenized-equity/grpc/types';
import { assetByCbrn } from '@/onchain/assets';
import {
  formatTimestamp,
  formatActionType,
} from '@/features/tokenized-equity/components/explore/equity-details/utils/corporateActionsFormatter';
import { useUpcomingEvents } from '@/features/tokenized-equity/hooks/useUpcomingEvents';
import { CorporateActionModal } from './CorporateActionModal';

export type UpcomingEvent = {
  id: string;
  eventLabel: string;
  tokenName: string;
  tokenSymbol: string;
  tokenImageUrl: string;
  executionDate: string;
  detail: string;
};

type EventRowProps = {
  event: UpcomingEvent;
  onClick: () => void;
};

// Error component for upcoming events
const UpcomingEventsError = memo(function UpcomingEventsError({ error }: { error: Error }) {
  return (
    <VStack paddingX={3} paddingY={3} alignItems="center" justifyContent="center" gap={1}>
      <TextBody as="p" color="fgMuted">
        Failed to load upcoming events
      </TextBody>
      <TextLabel1 as="p" color="fgMuted">
        {error.message || 'An unexpected error occurred'}
      </TextLabel1>
    </VStack>
  );
});

/**
 * Convert CorporateAction to UpcomingEvent format
 */
export function convertToDisplayEvent(action: CorporateAction): UpcomingEvent | null {
  const { details, action_type: actionType, id } = action;

  let cbrn: string | undefined;
  let date = 'N/A';
  let detail = 'N/A';

  // Extract data based on action type
  if (details.cash_dividend) {
    cbrn = details.cash_dividend.security.cbrn;
    date = formatTimestamp(details.cash_dividend.payout_date);
    detail = `$${details.cash_dividend.amount}/share`;
  } else if (details.stock_dividend) {
    cbrn = details.stock_dividend.security.cbrn;
    date = formatTimestamp(details.stock_dividend.paid_date);
    detail = `${details.stock_dividend.amount}/share`;
  } else if (details.stock_split) {
    cbrn = details.stock_split.security.cbrn;
    date = formatTimestamp(details.stock_split.effective_date);
    detail = details.stock_split.ratio;
  } else if (details.reverse_stock_split) {
    cbrn = details.reverse_stock_split.security.cbrn;
    date = formatTimestamp(details.reverse_stock_split.effective_date);
    detail = details.reverse_stock_split.ratio;
  }

  // Get asset info from cbrn
  const asset = assetByCbrn(cbrn);

  // If we can't find asset info, return null
  if (!asset) return null;

  return {
    id,
    eventLabel: formatActionType(actionType),
    tokenName: asset.name,
    tokenSymbol: asset.displaySymbol,
    tokenImageUrl: asset.imageUrl,
    executionDate: date,
    detail,
  };
}

// Event row component - clickable with caret icon
const EventRow = memo(function EventRow({ event, onClick }: EventRowProps) {
  return (
    <PressableOpacity onClick={onClick} noScaleOnPress>
      <HStack
        width="100%"
        paddingY={2}
        paddingX={3}
        alignItems="center"
        justifyContent="space-between"
        style={{ borderTop: '1px solid var(--color-line)' }}
      >
        {/* Left: Icon + Event info */}
        <HStack alignItems="center" gap={1.5}>
          <RemoteImage
            source={event.tokenImageUrl}
            size="m"
            shape="circle"
            alt={event.tokenSymbol}
          />
          <VStack alignItems="flex-start" gap={0}>
            <TextHeadline as="span">{event.eventLabel}</TextHeadline>
            <TextLabel1 as="span" color="fgMuted">
              {event.tokenSymbol}
            </TextLabel1>
          </VStack>
        </HStack>

        {/* Right: Date, detail, and caret icon */}
        <HStack alignItems="center" gap={2}>
          <VStack alignItems="flex-end" gap={0}>
            <TextBody as="span">{event.executionDate}</TextBody>
            <TextLabel1 as="span" color="fgMuted">
              {event.detail}
            </TextLabel1>
          </VStack>
          <Icon name="caretRight" size="s" color="fg" />
        </HStack>
      </HStack>
    </PressableOpacity>
  );
});

export const UpcomingEventsCard = memo(function UpcomingEventsCard() {
  const [selectedEvent, setSelectedEvent] = useState<UpcomingEvent | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  // Fetch and transform upcoming events
  const { events, isLoading, error } = useUpcomingEvents();

  const handleEventClick = useCallback(
    (event: UpcomingEvent) => () => {
      setSelectedEvent(event);
      setModalOpen(true);
    },
    [],
  );

  const handleModalClose = useCallback(() => {
    setModalOpen(false);
    setSelectedEvent(null);
  }, []);

  // Simple header - just the title
  const header = <TextTitle3 as="h2">Upcoming events</TextTitle3>;

  // Only show first 3 events in the sidebar card
  const displayEvents = events.slice(0, 3);

  // Show loading state
  if (isLoading) {
    return (
      <SummaryCard header={header} testID="upcoming-events-card">
        <VStack paddingX={3} paddingY={2}>
          <TextLabel2 as="p" color="fgMuted">
            Loading...
          </TextLabel2>
        </VStack>
      </SummaryCard>
    );
  }

  // Show error state
  if (error) {
    return (
      <SummaryCard header={header} testID="upcoming-events-card">
        <UpcomingEventsError error={error} />
      </SummaryCard>
    );
  }

  return (
    <>
      <SummaryCard header={header} testID="upcoming-events-card">
        {/* Subtitle */}
        <VStack paddingX={3} paddingBottom={1.5}>
          <TextLabel2 as="p" color="fgMuted">
            You have {events.length} upcoming event{events.length !== 1 ? 's' : ''}.
          </TextLabel2>
        </VStack>

        {/* Event rows - clickable with caret icons (show max 3) */}
        {displayEvents.map((event) => (
          <EventRow key={event.id} event={event} onClick={handleEventClick(event)} />
        ))}

        {/* "See all" link at bottom */}
        <VStack paddingX={3} paddingY={2}>
          <Link href="/portfolio/upcoming-events" color="fgPrimary" underline={false}>
            See all
          </Link>
        </VStack>
      </SummaryCard>

      {selectedEvent && (
        <CorporateActionModal event={selectedEvent} open={modalOpen} onClose={handleModalClose} />
      )}
    </>
  );
});
